/**
 * Dynamic-filter operands — a local copy of the small, stable constant set from
 * the legacy admin's domain-core (admin-v2 does not import from the legacy admin). Keep these ids in sync
 * with `Core/client/admin/src/shared/domain-core/filters/operands.js` — they are the wire
 * values persisted in the filter JSON (`op`).
 */
export const OP_EQUALS = '=';
export const OP_NOT_EQUALS = '!=';
export const OP_CONTAINS = 'contains';
export const OP_NOT_CONTAINS = 'not_contains';
export const OP_STARTS_WITH = 'starts_with';
export const OP_NOT_STARTS_WITH = 'not_starts_with';
export const OP_ENDS_WITH = 'ends_with';
export const OP_NOT_ENDS_WITH = 'not_ends_with';
export const OP_LIKE = 'like';
export const OP_NOT_LIKE = 'not_like';
export const OP_BETWEEN = 'between';
export const OP_NOT_BETWEEN = 'not_between';
export const OP_BETWEEN_EXCL = 'between_excluding';
export const OP_NOT_BETWEEN_EXCL = 'not_between_excluding';
export const OP_GREATER = '>';
export const OP_GREATER_EQ = '>=';
export const OP_LESS = '<';
export const OP_LESS_EQ = '<=';

export const OP_TYPE_BINARY = 'binary';
export const OP_TYPE_RANGE = 'range';

export interface Operand {
	id: string;
	label: string;
	type: typeof OP_TYPE_BINARY | typeof OP_TYPE_RANGE;
}

export const orderedValueOperands: Operand[] = [
	{ id: OP_EQUALS, label: '=', type: OP_TYPE_BINARY },
	{ id: OP_NOT_EQUALS, label: '≠', type: OP_TYPE_BINARY },
	{ id: OP_GREATER, label: '＞', type: OP_TYPE_BINARY },
	{ id: OP_GREATER_EQ, label: '⩾', type: OP_TYPE_BINARY },
	{ id: OP_LESS, label: '＜', type: OP_TYPE_BINARY },
	{ id: OP_LESS_EQ, label: '⩽', type: OP_TYPE_BINARY },
	{ id: OP_BETWEEN, label: 'Between', type: OP_TYPE_RANGE },
	{ id: OP_NOT_BETWEEN, label: 'Not Between', type: OP_TYPE_RANGE },
	{ id: OP_BETWEEN_EXCL, label: 'Between Excl.', type: OP_TYPE_RANGE },
	{ id: OP_NOT_BETWEEN_EXCL, label: 'Not Between Excl.', type: OP_TYPE_RANGE }
];

/** `metricTypes` conditions: comparisons plus inclusive Between (`value`..`valueTo`). */
export const metricOperands: Operand[] = orderedValueOperands.filter(o => o.type === OP_TYPE_BINARY || o.id === OP_BETWEEN);

export const searchTextOperands: Operand[] = [
	{ id: OP_EQUALS, label: '=', type: OP_TYPE_BINARY },
	{ id: OP_NOT_EQUALS, label: '≠', type: OP_TYPE_BINARY },
	{ id: OP_CONTAINS, label: 'Contains', type: OP_TYPE_BINARY },
	{ id: OP_NOT_CONTAINS, label: "Doesn't Contain", type: OP_TYPE_BINARY },
	{ id: OP_STARTS_WITH, label: 'Starts With', type: OP_TYPE_BINARY },
	{ id: OP_NOT_STARTS_WITH, label: "Doesn't Start With", type: OP_TYPE_BINARY },
	{ id: OP_ENDS_WITH, label: 'Ends With', type: OP_TYPE_BINARY },
	{ id: OP_NOT_ENDS_WITH, label: "Doesn't End With", type: OP_TYPE_BINARY },
	{ id: OP_LIKE, label: 'Like', type: OP_TYPE_BINARY },
	{ id: OP_NOT_LIKE, label: "Isn't Like", type: OP_TYPE_BINARY }
];

const allById = new Map<string, Operand>([...orderedValueOperands, ...searchTextOperands].map(o => [o.id, o]));
export const operandById = (id?: string): Operand | undefined => (id ? allById.get(id) : undefined);
